import type { ReactNode } from 'react'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import Grid from '@material-ui/core/Grid'
import { Box, Drawer } from '@material-ui/core'

import {
  Button,
  Card,
  DatePicker,
  Filters,
  FloatingButton,
  InfoBanner,
  LockedContentHeader,
  Select,
  Snackbar,
  Spinner,
  Tooltip,
  Typography,
  WindowTitle,
} from '@homeviews/shared-ui'
import type { FilterType } from '@homeviews/models'
import { CompanyStatsCard } from '@homeviews/companies'
import {
  AdditionalQuestionsChart,
  BtrAdditionalQuestionsChart,
  DEVELOPMENT_SCORES_CATEGORY_OPTIONS,
  DevelopmentScoresChart,
  DevelopmentScoresTableContainer,
  DonutChart,
  getRatingChangeMeta,
  OverallRatingHistoryChart,
  OwnerReviewsChart,
  RATING_TYPES_DETAILED,
  RatingsBreakdown,
  RatingsByChart,
  RatingsHistoryChart,
  REVIEWER_AGES,
  REVIEWER_TYPES,
  ReviewsHistoryChart,
  ScreenshotButton,
  SustainabilityChart,
  transformDevelopersOptions,
  transformDevelopmentScoresData,
  transformDevelopmentsOptions,
  transformRatingsByReviewerAgeData,
  transformRatingsReviewersTypeData,
  transformReviewerAgesData,
  transformReviewerTypesData,
} from '@homeviews/statistics'

import { filtersDateRange } from '@homeviews/utils'

import type { ITheme } from '@homeviews/styles'
import {
  clearStatisticsFilters,
  getUser,
  isEngageUser,
  isStandardDeveloperUser,
  selectDashboardLocalFilters,
  selectStatisticsFilters,
  setBulkStatisticsFilter,
  setDashboardLocalFilters,
  setStatisticsFilter,
  useGetDashboardDevelopersQuery,
  useGetDashboardDevelopmentsQuery,
  useGetDevelopmentsAverageQuery,
  useGetDevelopmentsBenchmarkQuery,
  useGetDevelopmentsStatsQuery,
  useGetReviewerTypesBenchmarkQuery,
  useGetReviewsStatsQuery,
  useGetSummaryStatsQuery,
} from '@homeviews/store'
import { mixpanelTrack } from '@homeviews/seo'
import { IS_COMPANY_GROUP_ADMIN } from '@homeviews/users'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      position: 'relative',
      padding: theme.spacing(
        theme.layout.contentHorizontalPadding,
        theme.layout.contentVerticalPadding,
      ),
    },
    header: {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      backgroundImage: 'url("assets/images/dashboard-background-mesh.jpg")',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '100%',
      paddingTop: theme.spacing(theme.layout.contentVerticalPadding),
      marginTop: -theme.spacing(theme.layout.contentVerticalPadding),
    },
    card: {
      height: '100%',
      position: 'relative',
      minHeight: 200,
    },
    companyCardWrapper: {
      paddingBottom: 35,
    },
    horizontalContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'space-between',
      marginBottom: 3,
      flexWrap: 'wrap',
    },
    horizontalContainerItem: {
      display: 'flex',
    },
    summaryCard: {
      minHeight: 'initial',
    },
    summaryItemTitle: {
      color: '#4E516B',
      fontSize: 24,
      fontWeight: 500,
      letterSpacing: '0.63px',
      lineHeight: '30px',
      marginRight: 3,
    },
    filteredDetailsContainer: {},
    filteredDetailsContainerAnchoredTop: {
      position: 'relative',
      marginBottom: '10px',
    },
    ratingDiff: {
      fontWeight: 500,
      transform: 'translate(-1px)',
    },
    summaryItem: {
      '& p': {
        display: 'inline-block',
      },
    },
    selectWrapper: {
      width: '200px',
      fontWeight: 500,
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(1.875 / 2),
      marginTop: theme.spacing(1.5 / 2),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        '& .DateRangePicker': {
          width: '100%',
        },
        '& .DateRangePickerInput': {
          width: '100%',
          justifyContent: 'space-between',
        },
        '& .DateInput_fang': {
          display: 'none',
        },
      },
    },
    filtersTitle: {
      color: theme.colors.grey600,
      fontWeight: 600,
      marginRight: 8,
    },
    selectWrapperButton: {
      width: 'initial',
      marginLeft: 10,
    },
    mobileFiltersHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      '& h3': {
        marginRight: 'auto',
      },
    },
    mobileFiltersDrawer: {
      '& .MuiDrawer-paper': {
        padding: theme.spacing(3.75),
        overflow: 'initial',
      },
    },
    label: {
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: '-0.2px',
      lineHeight: '17px',
      width: 'auto',
      marginRight: '2px',
      whiteSpace: 'nowrap',
    },
    filterSummaryText: {
      fontSize: 12,
      letterSpacing: '-0.2px',
      lineHeight: '17px',
      width: 'auto',
      marginRight: '10px',
    },
    filterRequiredText: {
      marginTop: 50,
      fontSize: theme.typography.pxToRem(24),
      fontWeight: 500,
      textAlign: 'center',
    },
    chartSource: {
      marginTop: 20,
      marginBottom: 20,
    },
    chartPlaceholder: {
      '& img': {
        width: '100%',
      },
    },
    topGrid: {
      '@media (min-width: 585px) and (max-width: 1700px)': {
        '& .MuiGrid-grid-sm-4': {
          maxWidth: '50%!important',
          flexBasis: '50%!important',
        },
        '& .c-dashboard__topGridTiles': {
          maxWidth: '100%!important',
          flexBasis: '100%!important',
        },
      },
    },
    topGridTiles: {
      '@media (min-width: 1200px) and (max-width: 1700px)': {
        '& .MuiGrid-grid-xs-6': {
          maxWidth: '25%!important',
          flexBasis: '25%!important',
        },
      },
    },
    donutChartGrid: {},
    gridHidden: {
      display: 'none!important',
    },
    cardHeaderWithFilter: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 5,
      '& h3': {
        marginRight: 10,
      },
      '& .c-select': {
        width: 182,
      },
    },
    dashboardTitleWrapper: {
      display: 'flex',
      marginBottom: 18,
      '& h1': {
        marginBottom: 0,
      },
      '& p': {
        marginLeft: 20,
        fontSize: 16,
        lineHeight: '17px',
        marginTop: 8,
      },
    },
  }),
)

const Dashboard: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const activeFilters = useSelector(selectStatisticsFilters)
  const user = useSelector(getUser)
  const isCompanyGroupAdmin = IS_COMPANY_GROUP_ADMIN(user)
  const activeDashboardLocalFilters = useSelector(selectDashboardLocalFilters)
  let isStandardDeveloper = useSelector(isStandardDeveloperUser)
  const isEngagePackage = Boolean(useSelector(isEngageUser))
  if (isEngagePackage === true) {
    isStandardDeveloper = false
  }
  // A CompanyGroupAdmin gets full dashboard access like an admin, regardless of
  // their own status or any company's premium status.
  if (isCompanyGroupAdmin) {
    isStandardDeveloper = false
  }

  const [activeDevs, setActiveDevs] = useState('')
  const [activeCompanies, setActiveCompanies] = useState('')
  const [reviewerType, setReviewerType] = useState('')
  const [datePeriod, setDatePeriod] = useState<any>({})

  const [localFilters, setLocalFilters] = useState({})

  const [snackbarVisible, setSnackbarVisible] = useState<{
    open: boolean
    message?: string
    status?: 'success' | 'error' | 'info'
    content?: ReactNode
  }>({
    open: false,
    message: '',
    status: undefined,
    content: null,
  })

  useEffect(() => {
    if (activeFilters?.['reviewer_ages[]']?.length) {
      setSnackbarVisible({
        open: true,
        status: 'info',
        content: (
          <>
            <Typography variant="h4">
              Data shown will be impacted/limited.
            </Typography>
            <Typography>
              Please note that the currently selected filter(s) are impacting
              the data shown. This is due to the filter(s) and date range
              selected overlapping with the start date from which HomeViews
              started to collect reviewer age ranges and/or reviewer type. To
              see a more complete picture, either deselect the age filter or
              reviewer type, or adjust the date range to begin after March 2021.
            </Typography>
          </>
        ),
      })
    } else {
      setSnackbarVisible({
        open: false,
      })
    }
  }, [activeFilters?.['reviewer_ages[]']])

  useEffect(() => {
    if (activeDashboardLocalFilters.activeDevs !== activeDevs) {
      setActiveDevs(activeDashboardLocalFilters.activeDevs)
    }
    if (activeDashboardLocalFilters.activeCompanies !== activeCompanies) {
      setActiveCompanies(activeDashboardLocalFilters.activeCompanies)
    }
    if (activeDashboardLocalFilters.reviewerType !== reviewerType) {
      setReviewerType(activeDashboardLocalFilters.reviewerType)
    }
    if (activeDashboardLocalFilters.datePeriod !== datePeriod) {
      setDatePeriod(activeDashboardLocalFilters.datePeriod)
    }
  }, [])

  useEffect(() => {
    dispatch(
      setDashboardLocalFilters({
        filters: {
          activeDevs,
          activeCompanies,
          reviewerType,
          datePeriod,
        },
      }),
    )
  }, [
    activeDevs,
    activeCompanies,
    datePeriod?.dateFrom,
    datePeriod?.dateTo,
    reviewerType,
  ])

  const reviewerTypesOptionsRef = useRef([])

  const developmentScoresGrid = useRef(null)
  const reviewerTypesGrid = useRef(null)
  const reviewerAgesGrid = useRef(null)
  const ratingsByAgeGrid = useRef(null)
  const ratingsReviewerTypeGrid = useRef(null)
  const reviewsHistoryGrid = useRef(null)
  const ratingsHistoryGrid = useRef(null)
  const btrQuestionsGrid = useRef(null)
  const additionsQuestionsGrid = useRef(null)
  const btsQuestionsGrid = useRef(null)
  const btsAdditionsQuestionsGrid = useRef(null)
  const ownerReviewsGrid = useRef(null)
  const sustainabilityChartGrid = useRef(null)
  const developmentScoresTableGrid = useRef(null)
  const overallRatingHistoryChart = useRef(null)

  const statsPeriod = activeFilters?.date_from ? 'period' : 'month'

  const [mobileFiltersOpened, setMobileFiltersOpened] = useState<boolean>(false)

  let onlyAccessibleDevelopment = null

  const {
    data: dashboardDevelopers,
    isFetching: isFetchingDashboardDevelopers,
  } = useGetDashboardDevelopersQuery(
    activeFilters?.['development[]']
      ? {
          'development[]': activeFilters?.['development[]'],
          engage: isEngagePackage,
        }
      : { engage: isEngagePackage },
  )

  const dashboardDevelopersOptions = useMemo(
    () => transformDevelopersOptions(dashboardDevelopers),
    [dashboardDevelopers],
  )

  // A CompanyGroupAdmin narrows the (aggregated) dashboard to one of their
  // assigned companies. Source the options from the companies on the user
  // (whoami) rather than the publish-filtered dashboard-developers endpoint,
  // so every overseen company is selectable.
  const developerSelectorOptions = isCompanyGroupAdmin
    ? (user?.developers ?? []).map((developer) => ({
        value: developer.id,
        label: developer.name,
      }))
    : dashboardDevelopersOptions

  const { data: dashboardDevelopments } = useGetDashboardDevelopmentsQuery(
    activeFilters?.['developer[]']
      ? {
          'developer[]': activeFilters?.['developer[]'],
          engage: isEngagePackage,
        }
      : { engage: isEngagePackage },
  )

  // --------------------------------------------------------------------------------
  // Using dashboardDevelopments data from API call /dashboard-developments,
  // maps the available development type names to benchmark types, so if there's a
  // single unique benchmark type it can be passed to Charts/Graphs
  // to conditionally set tooltip/label copy.
  // Also, if only development filters selected, only uses these developments as source.
  const dmentIdsToBenchmarkType = useMemo(() => {
    if (dashboardDevelopments) {
      const DmentTypeIdsToBenchmarkType = {
        1: 'Build To Rent',
        4: 'Build To Sell', // Actual Build To Sell (BTS) ID
        5: '', // Actually Housing Association, but uses UK benchmark
        6: 'Build To Sell', // Actually Housing Development, but uses BTS benchmark
        7: 'Build To Sell', // Actually Later Living, but uses BTS benchmark
      }
      const map: Record<number, string> = {}
      dashboardDevelopments.data.forEach((dment) => {
        // Fallback to empty string for UK benchmark
        map[dment.id] =
          DmentTypeIdsToBenchmarkType[dment.development_type_id || '']
      })
      return map
    }
  }, [dashboardDevelopments])
  const uniqBenchmarkTypes = useMemo(() => {
    if (dmentIdsToBenchmarkType) {
      return [...new Set(Object.values(dmentIdsToBenchmarkType))]
    }
  }, [dmentIdsToBenchmarkType])
  const globalBenchmarkType = useMemo(() => {
    // Start active benchmark types as all dashboardDevelopments from API
    let activeBenchmarkTypes = uniqBenchmarkTypes
    // If filter developments selected, only use these
    const filterDmentIds = activeFilters['development[]']
    if (filterDmentIds) {
      const filterBenchmarkTypes: string[] = filterDmentIds.map(
        (id) => dmentIdsToBenchmarkType[id],
      )
      activeBenchmarkTypes = [...new Set(filterBenchmarkTypes)]
    }
    // If only 1 unique active benchmark type, then global type is that
    if (activeBenchmarkTypes && activeBenchmarkTypes.length === 1) {
      return activeBenchmarkTypes[0]
    } else {
      return null
    }
  }, [uniqBenchmarkTypes, activeFilters['development[]']])
  // --------------------------------------------------------------------------------

  const dashboardDevelopmentsOptions = useMemo(
    () => transformDevelopmentsOptions(dashboardDevelopments?.data),
    [dashboardDevelopments],
  )

  onlyAccessibleDevelopment =
    dashboardDevelopmentsOptions?.length === 1
      ? dashboardDevelopmentsOptions[0]
      : null

  // Disable loading graphs if more than 2 developers available and `developer` filter not set
  const developersFilterRequired = useCallback(() => {
    // A CompanyGroupAdmin sees their companies aggregated by default and can
    // optionally narrow with the Developer selector, so a filter is never
    // required for them to load the charts.
    if (isCompanyGroupAdmin) {
      return false
    }
    if (
      (!!dashboardDevelopersOptions?.length &&
        dashboardDevelopersOptions.length > 2 &&
        (!activeFilters?.['developer[]'] ||
          !activeFilters?.['developer[]']?.length) &&
        (!activeFilters?.['development[]'] ||
          !activeFilters?.['development[]']?.length)) ||
      isFetchingDashboardDevelopers
    ) {
      return true
    }

    return false
  }, [
    isCompanyGroupAdmin,
    dashboardDevelopersOptions,
    activeFilters,
    isFetchingDashboardDevelopers,
  ])

  const graphDisabled = developersFilterRequired()

  const { data: summaryStats, isFetching: isFetchingSummaryStats } =
    useGetSummaryStatsQuery(
      {
        ...activeFilters,
        ...localFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled,
      },
    )

  const {
    data: reviewerTypes,
    isFetching: isFetchingReviewerTypes,
    isLoading: isLoadingReviewerTypes,
  } = useGetReviewsStatsQuery(
    {
      group_by: 'reviewer_type',
      aggregate: 'count',
      ...activeFilters,
      engage: isEngagePackage,
    },
    {
      skip: graphDisabled || isStandardDeveloper,
    },
  )

  const {
    data: ratingsReviewerTypes,
    isFetching: isFetchingRatingsReviewerTypes,
  } = useGetReviewsStatsQuery(
    {
      group_by: 'reviewer_type',
      aggregate: 'avg',
      ...activeFilters,
      engage: isEngagePackage,
    },
    {
      skip: graphDisabled || isStandardDeveloper,
    },
  )

  const [reviewerTypesData, reviewerTypesOptions] = useMemo(
    () => transformReviewerTypesData(reviewerTypes?.data),
    [reviewerTypes?.data],
  )
  reviewerTypesOptionsRef.current = reviewerTypesOptions

  const ratingsReviewersTypeData = useMemo(
    () => transformRatingsReviewersTypeData(ratingsReviewerTypes?.data),
    [ratingsReviewerTypes?.data],
  )

  const { data: reviewerAges, isFetching: isFetchingReviewerAges } =
    useGetReviewsStatsQuery(
      {
        group_by: 'age-range',
        aggregate: 'count',
        ...activeFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled || isStandardDeveloper,
      },
    )

  const reviewerAgesData = useMemo(
    () => transformReviewerAgesData(reviewerAges?.data),
    [reviewerAges?.data],
  )

  const {
    data: ratingsByReviewerAge,
    isFetching: isFetchingRatingsByReviewerAge,
  } = useGetReviewsStatsQuery(
    {
      group_by: 'age-range',
      aggregate: 'avg',
      ...activeFilters,
      engage: isEngagePackage,
    },
    {
      skip: graphDisabled || isStandardDeveloper,
    },
  )

  const ratingsByReviewerAgeData = useMemo(
    () => transformRatingsByReviewerAgeData(ratingsByReviewerAge?.data),
    [ratingsByReviewerAge?.data],
  )

  const {
    data: developmentsScores,
    isFetching: isFetchingDevelopmentsScoresData,
  } = useGetDevelopmentsStatsQuery(
    {
      ...activeFilters,
      engage: isEngagePackage,
    },
    {
      skip: graphDisabled || isStandardDeveloper,
    },
  )

  const { data: developmentsAverageData } = useGetDevelopmentsAverageQuery(
    {
      ...activeFilters,
      engage: isEngagePackage,
    },
    {
      skip: graphDisabled || isStandardDeveloper,
    },
  )

  const { data: developmentsBenchmarkData } = useGetDevelopmentsBenchmarkQuery(
    {
      ...activeFilters,
    },
    {
      skip: graphDisabled || isStandardDeveloper,
    },
  )

  const { data: reviewerTypesBenchmarkData } =
    useGetReviewerTypesBenchmarkQuery(
      {
        ...activeFilters,
      },
      {
        skip: graphDisabled || isStandardDeveloper,
      },
    )

  const [developmentScoresData, singleDevelopmentScoresChart] = useMemo(
    () => transformDevelopmentScoresData(developmentsScores?.data),
    [developmentsScores?.data],
  )

  const totalReviews = summaryStats?.total_reviews?.value
  const {
    diffText: totalReviewsDiffText,
    diffCssClass: totalReviewsDiffCssClass,
  } = getRatingChangeMeta(summaryStats?.total_reviews?.abs_change, statsPeriod)

  const verifiedReviews = summaryStats?.verified_reviews?.value
  const {
    diffText: verifiedReviewsDiffText,
    diffCssClass: verifiedReviewsDiffCssClass,
  } = getRatingChangeMeta(
    summaryStats?.verified_reviews?.abs_change,
    statsPeriod,
  )

  const nonVerifiedReviews = summaryStats?.non_verified_reviews?.value
  const {
    diffText: nonVerifiedReviewsDiffText,
    diffCssClass: nonVerifiedReviewsDiffCssClass,
  } = getRatingChangeMeta(
    summaryStats?.non_verified_reviews?.abs_change,
    statsPeriod,
  )

  const replyRate = summaryStats?.reply_rate?.value

  const handleDateChange = useCallback(
    (newData: { date_from: string; date_to: string }) => {
      setDatePeriod({
        dateFrom: newData?.date_from,
        dateTo: newData?.date_to,
      })
      dispatch(
        setStatisticsFilter({
          filter: 'date_from',
          value: newData?.date_from,
        }),
      )
      dispatch(
        setStatisticsFilter({
          filter: 'date_to',
          value: newData?.date_to,
        }),
      )

      if (
        activeFilters.date_from !== newData?.date_from ||
        activeFilters.date_to !== newData?.date_to
      ) {
        mixpanelTrack('DASHBOARD_DATE_FILTER', {
          'From Date': newData?.date_from,
          'To Date': newData?.date_to,
        })
      }
    },
    [dispatch],
  )

  const handleClearFilters = useCallback(() => {
    setReviewerType('')
    setActiveDevs('')
    setActiveCompanies('')

    // Reset chart-local filters like Rating Breakdown filter
    setLocalFilters({})
    dispatch(clearStatisticsFilters())
    mixpanelTrack('DASHBOARD_RESET_FILTERS')
  }, [dispatch])

  const handleQuickPickDateChange = useCallback(
    (newDateRange) => {
      dispatch(
        setBulkStatisticsFilter([
          {
            filter: 'date_from',
            value: newDateRange.value,
          },
          {
            filter: 'date_to',
            value: newDateRange.endDate,
          },
        ]),
      )
      mixpanelTrack('DASHBOARD_DATE_RANGE_FILTER', {
        'Preset Date Range': newDateRange.label,
        'From Date': newDateRange.value,
        'To Date': newDateRange.endDate,
      })
    },
    [dispatch],
  )

  const handleSelectFilterChange = useCallback(
    (filterType: FilterType) => (option) => {
      if (filterType === 'reviewer_type') {
        setReviewerType(option?.label)
        mixpanelTrack('DASHBOARD_REVIEWER_TYPE_FILTER', {
          'Reviewer Type': option?.label,
        })
      } else if (filterType === 'owner_type[]') {
        mixpanelTrack('DASHBOARD_OWNER_TYPE_FILTER', {
          'Owner Type': option?.label,
        })
      }
      dispatch(
        setStatisticsFilter({
          filter: filterType,
          value: option?.value || false,
        }),
      )
    },
    [dispatch],
  )

  const handleSelectLocalFilterChange =
    (filterType: FilterType) => (option) => {
      setLocalFilters({
        ...localFilters,
        [filterType]: option?.value,
      })

      mixpanelTrack('DASHBOARD_CHART_FILTER_CHANGE', {
        'Chart Name': 'Ratings Breakdown',
        Filter: option?.label || '',
      })
    }

  const handleMultiSelectFilterChange = useCallback(
    (filterType: FilterType) => (option) => {
      const newOptions = []
      const newLabels = []
      if (option) {
        option.forEach((x) => {
          if (x.value) {
            newOptions.push(x.value)
            newLabels.push(x.label)
          }
        })
      }
      if (filterType === 'reviewer_type[]') {
        setReviewerType(newLabels.join(', '))
        mixpanelTrack('DASHBOARD_REVIEWER_TYPE_FILTER', {
          value: newLabels,
        })
      }
      if (filterType === 'development[]') {
        let truncatedDevelopmentLabels = false
        if (newLabels?.length > 7) {
          truncatedDevelopmentLabels = true
        }
        const truncatedDevelopmentsCount = newLabels?.length - 7
        const developmentsFilterLabel = truncatedDevelopmentLabels
          ? `${newLabels
              .slice(0, 7)
              .join(', ')} and the ${truncatedDevelopmentsCount} ${
              truncatedDevelopmentsCount === 1 ? 'other' : 'others'
            }`
          : newLabels.join(', ')
        setActiveDevs(developmentsFilterLabel)
        mixpanelTrack('DASHBOARD_DEVELOPMENT_FILTER', {
          'Development Name': newLabels,
        })
      }
      if (filterType === 'reviewer_ages[]') {
        mixpanelTrack('DASHBOARD_AGE_FILTER', {
          'Reviewer Age': newLabels,
        })
      }
      if (filterType === 'developer[]') {
        let truncatedCompaniesLabels = false
        if (newLabels?.length > 7) {
          truncatedCompaniesLabels = true
        }
        const truncatedCompaniesCount = newLabels?.length - 7
        const companiesFilterLabel = truncatedCompaniesLabels
          ? `${newLabels
              .slice(0, 7)
              .join(', ')} and the ${truncatedCompaniesCount} ${
              truncatedCompaniesCount === 1 ? 'other' : 'others'
            }`
          : newLabels.join(', ')
        setActiveCompanies(companiesFilterLabel)

        dispatch(
          setBulkStatisticsFilter([
            {
              filter: filterType,
              value: newOptions,
            },
            {
              filter: 'development[]',
              value: null,
            },
          ]),
        )
      } else if (filterType === 'reviewer_type[]') {
        dispatch(
          setBulkStatisticsFilter([
            {
              filter: filterType,
              value: newOptions,
            },
            {
              filter: 'owner_type[]',
              value: null,
            },
          ]),
        )
      } else {
        dispatch(
          setStatisticsFilter({
            filter: filterType,
            value: newOptions,
          }),
        )
      }
    },
    [dispatch],
  )

  const renderSelectDropdowns = useCallback(
    () => (
      <>
        <div className={classes.selectWrapper}>
          <Select
            placeholder="Developments"
            options={dashboardDevelopmentsOptions || []}
            onChange={handleMultiSelectFilterChange('development[]')}
            value={activeFilters?.['development[]']}
            isClearable
            isMulti
            withCheckboxes
          />
        </div>

        {!!developerSelectorOptions?.length &&
          developerSelectorOptions.length > 1 && (
            <div className={classes.selectWrapper}>
              <Select
                placeholder="Developer"
                options={developerSelectorOptions || []}
                onChange={handleMultiSelectFilterChange('developer[]')}
                value={activeFilters?.['developer[]'] || []}
                isClearable
                isMulti
                withCheckboxes
              />
            </div>
          )}

        <div className={classes.selectWrapper}>
          <Select
            placeholder="Reviewer Type"
            // WHEN there are selected-filter-developments/developers THEN show revTypesOptions
            // ELSE IF only 1 dashboard-developers option (Developer user logged in) THEN show revTypeOptions
            // ELSE show no options
            options={
              // A CompanyGroupAdmin sees aggregated data by default, so keep
              // the reviewer-type filter usable without first picking a
              // developer/development.
              activeDevs || activeCompanies || isCompanyGroupAdmin
                ? reviewerTypesOptionsRef.current
                : dashboardDevelopersOptions.length === 1
                ? reviewerTypesOptionsRef.current
                : []
            }
            onChange={handleMultiSelectFilterChange('reviewer_type[]')}
            value={activeFilters?.['reviewer_type[]'] || []}
            isClearable
            isMulti
            withCheckboxes
          />
        </div>
        {(activeFilters?.['reviewer_type[]']?.find?.(
          (filter: any) => filter === 'owner',
        ) ||
          activeFilters?.['reviewer_type[]']?.find?.(
            (filter: any) => filter === 'shared_owner',
          )) && (
          <div className={classes.selectWrapper}>
            <Select
              placeholder="Owner Type"
              options={[
                {
                  label: 'First Time Buyer',
                  value: 'first_time',
                },
                {
                  label: 'Help to Buy',
                  value: 'help_to_buy',
                },
              ]}
              onChange={handleSelectFilterChange('owner_type[]')}
              value={activeFilters?.['owner_type[]']}
              isClearable
            />
          </div>
        )}
        <div className={classes.selectWrapper}>
          <Select
            placeholder="Reviewer Age"
            options={Object.values(REVIEWER_AGES)}
            onChange={handleMultiSelectFilterChange('reviewer_ages[]')}
            value={activeFilters?.['reviewer_ages[]']}
            isClearable
            isMulti
            withCheckboxes
          />
        </div>
        <div>
          <DatePicker
            initialStartDate={activeFilters?.['date_from'] || null}
            initialEndDate={activeFilters?.['date_to'] || null}
            onChange={handleDateChange}
            withQuickPick
            onQuickPickOptionsChange={handleQuickPickDateChange}
            {...(isMobile && {
              verticalHeight: 370,
              orientation: 'vertical',
              openDirection: 'top',
              anchorDirection: 'bottom',
            })}
          />
        </div>
      </>
    ),
    [
      activeFilters,
      classes.selectWrapper,
      handleDateChange,
      handleQuickPickDateChange,
      handleSelectFilterChange,
      isMobile,
      isCompanyGroupAdmin,
      dashboardDevelopersOptions,
      developerSelectorOptions,
      dashboardDevelopmentsOptions,
      handleMultiSelectFilterChange,
    ],
  )

  const chartSource = () => (
    <div className={classes.chartSource}>
      <Typography>Source: HomeViews</Typography>
    </div>
  )

  const getFilteredDetails = (
    development,
    dateRange,
    type,
    oneLine = false,
    additionalFilters = [],
    getDataAsArray = false,
  ) => {
    const { dateFrom, dateTo } = filtersDateRange(
      activeFilters?.date_from,
      activeFilters?.date_to,
    )
    const formattedDateRange =
      !dateFrom || !dateTo ? '' : `${dateFrom} - ${dateTo}`

    let formattedAgeRange = ''

    if (activeFilters?.['reviewer_ages[]']) {
      const reviewerAgeLabelData: any = Object.values(REVIEWER_AGES).filter(
        (item: any) => activeFilters['reviewer_ages[]']?.includes?.(item.value),
      )

      if (reviewerAgeLabelData?.length) {
        formattedAgeRange = reviewerAgeLabelData
          .map((item: any) => item.label)
          .join(', ')
      }
    }

    if (getDataAsArray) {
      return [
        {
          label: 'Filtered by',
          value: onlyAccessibleDevelopment?.label || development || 'All',
        },
        {
          label: 'Developers',
          value: activeCompanies || 'All',
        },
        {
          label: 'Date Range',
          value: formattedDateRange || 'All time',
        },
        {
          label: 'Resident Type',
          value: type || 'All',
        },
        {
          label: 'Age Range',
          value: formattedAgeRange || 'All',
        },
      ]
    }

    return (
      <div
        className={clsx(
          oneLine === true
            ? classes.filteredDetailsContainerAnchoredTop
            : classes.filteredDetailsContainer,
        )}
      >
        {oneLine === false ? (
          <div className={classes.horizontalContainer}>
            <div className={classes.horizontalContainerItem}>
              <Typography className={classes.label}>Filtered by</Typography>
              <Typography className={classes.filterSummaryText}>
                {onlyAccessibleDevelopment?.label || development || 'All'}
              </Typography>
            </div>
            {!!activeCompanies && (
              <div className={classes.horizontalContainerItem}>
                <Typography className={classes.label}>Developers</Typography>
                <Typography className={classes.filterSummaryText}>
                  {activeCompanies || 'All'}
                </Typography>
              </div>
            )}
            <div className={classes.horizontalContainerItem}>
              <Typography className={classes.label}>Date Range</Typography>
              <Typography className={classes.filterSummaryText}>
                {formattedDateRange || 'All time'}
              </Typography>
            </div>
            <div className={classes.horizontalContainerItem}>
              <Typography className={classes.label}>Resident Type</Typography>
              <Typography className={classes.filterSummaryText}>
                {type || 'All'}
              </Typography>
            </div>
            <div className={classes.horizontalContainerItem}>
              <Typography className={classes.label}>Age Range</Typography>
              <Typography className={classes.filterSummaryText}>
                {formattedAgeRange || 'All'}
              </Typography>
            </div>
            <div className={classes.horizontalContainerItem}>
              {!!additionalFilters.length &&
                additionalFilters
                  .filter((filter: any) => !!filter)
                  .map((filter: any) => (
                    <Fragment key={filter.label}>
                      <Typography className={classes.label}>
                        {filter.label}
                      </Typography>
                      <Typography className={classes.filterSummaryText}>
                        {filter.value}
                      </Typography>
                    </Fragment>
                  ))}
            </div>
          </div>
        ) : (
          <div className={classes.horizontalContainer}>
            <Typography className={classes.label}>Filtered by</Typography>
            <Typography className={classes.filterSummaryText}>
              {onlyAccessibleDevelopment?.label || development || 'All'}
            </Typography>
            {!!activeCompanies && (
              <div className={classes.horizontalContainerItem}>
                <Typography className={classes.label}>Developers</Typography>
                <Typography className={classes.filterSummaryText}>
                  {activeCompanies || 'All'}
                </Typography>
              </div>
            )}
            <Typography className={classes.label}>Date Range</Typography>
            <Typography className={classes.filterSummaryText}>
              {formattedDateRange || 'All time'}
            </Typography>

            <Typography className={classes.label}>Resident Type</Typography>
            <Typography className={classes.filterSummaryText}>
              {type || 'All'}
            </Typography>

            <Typography className={classes.label}>Age Range</Typography>
            <Typography className={classes.filterSummaryText}>
              {formattedAgeRange || 'All'}
            </Typography>
          </div>
        )}
      </div>
    )
  }

  const hasDonutChart = () => {
    if (reviewerTypesData?.length < 2 && !isLoadingReviewerTypes) {
      return false
    }

    return true
  }

  const hasAgeRangeDonutChart = () => {
    if (reviewerAgesData?.length < 2 && !isFetchingReviewerAges) {
      return false
    }

    return true
  }

  const handleSnackbarClose = () => {
    setSnackbarVisible({
      open: false,
    })
  }

  function ratingsBreakdownFiltersDetails() {
    const ratingsBreakdownFilter = DEVELOPMENT_SCORES_CATEGORY_OPTIONS.find(
      (filter: any) =>
        filter.value === localFilters?.['rating_breakdown_category'],
    )
    if (!ratingsBreakdownFilter) {
      return {
        label: 'Ratings type',
        value: 'Overall Review Rating',
      }
    }

    return {
      label: 'Ratings type',
      value: ratingsBreakdownFilter.label,
    }
  }

  return (
    <>
      <WindowTitle title="Dashboard" />
      <div className={classes.root}>
        <div className={classes.header}>
          {isStandardDeveloper ||
            (isEngagePackage && (
              <InfoBanner theme="gold" upgrade>
                <Typography className="l-infoBanner__title">
                  Upgrade your {isEngagePackage ? 'Engage' : null} account today
                </Typography>
              </InfoBanner>
            ))}

          {isEngagePackage ? (
            <div className={classes.dashboardTitleWrapper}>
              <Typography variant="h1">Demo Dashboard</Typography>
              <Typography>
                This dashboard is a demo account and contains dummy data but
                shows the real features our partners have access to.
              </Typography>
            </div>
          ) : (
            <div className={classes.dashboardTitleWrapper}>
              <Typography variant="h1">Dashboard</Typography>
              <Typography>
                Your dashboard shows the profiles you have been granted access
                to.
              </Typography>
            </div>
          )}

          {!isMobile && (
            <Filters isLoading={false}>
              <Typography className={classes.filtersTitle}>Filters</Typography>
              {renderSelectDropdowns()}
              <div
                className={clsx(
                  classes.selectWrapper,
                  classes.selectWrapperButton,
                )}
              >
                <Button
                  variant="outlined"
                  customColor="dark"
                  onClick={handleClearFilters}
                >
                  Reset filters
                </Button>
              </div>
            </Filters>
          )}
        </div>

        {developersFilterRequired() ? (
          <Typography className={classes.filterRequiredText}>
            {!isFetchingDashboardDevelopers &&
              'Set developer or development filter'}
          </Typography>
        ) : (
          <Grid container spacing={3} className={classes.topGrid}>
            {isFetchingSummaryStats ? (
              <Grid item xs={12}>
                <Card className={classes.card}>
                  <Spinner centered />
                </Card>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={4}>
                  <Card
                    className={clsx(classes.card, classes.companyCardWrapper)}
                  >
                    <CompanyStatsCard
                      name={summaryStats?.developer?.name || ''}
                      hasDeveloper={!!summaryStats?.developer}
                      logoUrl={summaryStats?.developer?.logo}
                      no_of_reviews={summaryStats?.summary?.reviews || 0}
                      developerRating={summaryStats?.summary?.rating || '0'}
                      rating={summaryStats?.summary?.rating || '0'}
                      rating_overall={summaryStats?.summary?.rating_overall}
                      rating_facilities={
                        summaryStats?.summary?.rating_facilities
                      }
                      rating_design={summaryStats?.summary?.rating_design}
                      rating_location={summaryStats?.summary?.rating_location}
                      rating_value={summaryStats?.summary?.rating_value}
                      rating_management={
                        summaryStats?.summary?.rating_management
                      }
                    />
                    {getFilteredDetails(activeDevs, datePeriod, reviewerType)}
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card
                    className={classes.card}
                    header={
                      <div className={classes.cardHeaderWithFilter}>
                        <Typography variant="h3">Ratings Breakdown</Typography>
                        <Select
                          options={DEVELOPMENT_SCORES_CATEGORY_OPTIONS}
                          onChange={handleSelectLocalFilterChange(
                            'rating_breakdown_category',
                          )}
                          value={
                            localFilters?.['rating_breakdown_category'] ||
                            'rating'
                          }
                          alignRight
                        />
                      </div>
                    }
                  >
                    <RatingsBreakdown
                      ratings={summaryStats?.rating_breakdown || []}
                      statsPeriod={statsPeriod}
                    />
                    {getFilteredDetails(
                      activeDevs,
                      datePeriod,
                      reviewerType,
                      false,
                      [ratingsBreakdownFiltersDetails()],
                    )}
                  </Card>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  className={clsx(
                    classes.topGridTiles,
                    'c-dashboard__topGridTiles',
                  )}
                >
                  <Grid
                    container
                    spacing={3}
                    style={{
                      height: 'calc(100% + 24px)',
                    }}
                  >
                    <Grid item xs={6}>
                      <Card
                        className={clsx(classes.card, classes.summaryCard)}
                        header={
                          <Typography variant="h4">Total Reviews</Typography>
                        }
                      >
                        <div className={classes.summaryItem}>
                          <Typography className={classes.summaryItemTitle}>
                            {totalReviews}
                          </Typography>
                          <Typography
                            className={clsx(
                              classes.ratingDiff,
                              totalReviewsDiffCssClass,
                            )}
                          >
                            {totalReviewsDiffText}
                          </Typography>
                        </div>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card
                        className={clsx(classes.card, classes.summaryCard)}
                        header={
                          <Typography variant="h4">Verified Reviews</Typography>
                        }
                      >
                        <div className={classes.summaryItem}>
                          <Typography className={classes.summaryItemTitle}>
                            {verifiedReviews}
                          </Typography>
                          <Typography
                            className={clsx(
                              classes.ratingDiff,
                              verifiedReviewsDiffCssClass,
                            )}
                          >
                            {verifiedReviewsDiffText}
                          </Typography>
                        </div>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card
                        className={clsx(classes.card, classes.summaryCard)}
                        header={
                          <Typography variant="h4">
                            Non-verified Reviews
                          </Typography>
                        }
                      >
                        <div className={classes.summaryItem}>
                          <Typography className={classes.summaryItemTitle}>
                            {nonVerifiedReviews}
                          </Typography>
                          <Typography
                            className={clsx(
                              classes.ratingDiff,
                              nonVerifiedReviewsDiffCssClass,
                            )}
                          >
                            {nonVerifiedReviewsDiffText}
                          </Typography>
                        </div>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card
                        className={clsx(classes.card, classes.summaryCard)}
                        header={
                          <Typography variant="h4">Your reply rate</Typography>
                        }
                      >
                        <div className={classes.summaryItem}>
                          <Typography className={classes.summaryItemTitle}>
                            {replyRate}%
                          </Typography>
                        </div>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid item xs={12} ref={developmentScoresGrid}>
              <Card
                className={classes.card}
                eventName="DEVELOPMENT_SCORES_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Development Scores
                      </Typography>
                      <ScreenshotButton />
                      <Tooltip
                        title="Development Scores Chart"
                        text="Discover how each of your developments’ scores compare for each category on HomeViews as well as how these scores benchmark against the HomeViews UK Average and your overall portfolio score."
                      />
                    </div>
                  </LockedContentHeader>
                }
              >
                {isFetchingDevelopmentsScoresData ? (
                  <Spinner centered />
                ) : (
                  <>
                    {!isStandardDeveloper && (
                      <>
                        <DevelopmentScoresChart
                          data={developmentScoresData}
                          developmentsAverageData={developmentsAverageData}
                          developmentsBenchmarkData={developmentsBenchmarkData}
                          gridRef={developmentScoresGrid?.current}
                          singleDevelopmentScoresChart={
                            !!singleDevelopmentScoresChart
                          }
                          filtersDetails={getFilteredDetails(
                            activeDevs,
                            datePeriod,
                            reviewerType,
                            true,
                          )}
                          developmentType={globalBenchmarkType}
                        />
                        {chartSource()}
                      </>
                    )}
                    {isStandardDeveloper && (
                      <div className={classes.chartPlaceholder}>
                        <img
                          src="assets/images/charts/chart-development-scores-greyed.png"
                          alt="Chart placeholder"
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              ref={reviewerTypesGrid}
              className={clsx(
                classes.donutChartGrid,
                !hasDonutChart() && classes.gridHidden,
              )}
            >
              <Card
                className={classes.card}
                eventName="REVIEWER_TYPES_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Reviewer Types
                      </Typography>
                      <ScreenshotButton />
                    </div>
                  </LockedContentHeader>
                }
              >
                {isFetchingReviewerTypes ? (
                  <Spinner centered />
                ) : (
                  <>
                    {!isStandardDeveloper && (
                      <>
                        {getFilteredDetails(
                          activeDevs,
                          datePeriod,
                          reviewerType,
                          true,
                        )}
                        <DonutChart
                          data={reviewerTypesData}
                          gridRef={reviewerTypesGrid?.current}
                          chartType="default"
                        />
                        {chartSource()}
                      </>
                    )}
                    {isStandardDeveloper && (
                      <div className={classes.chartPlaceholder}>
                        <img
                          src="assets/images/charts/chart-reviewer-types-greyed.png"
                          alt="Chart placeholder"
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Grid>
            <Grid
              item
              xs={12}
              sm={!hasDonutChart() ? 12 : 6}
              ref={ratingsReviewerTypeGrid}
            >
              <Card
                className={classes.card}
                eventName="RATINGS_BY_REVIEWER_TYPE_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Ratings by Reviewer Type
                      </Typography>
                      <ScreenshotButton />
                      <Tooltip
                        title="Ratings by reviewer type"
                        text="Click the different resident types at the bottom of the graph to see how they each score their experiences and how this compares to the HomeViews Benchmark."
                      />
                    </div>
                  </LockedContentHeader>
                }
              >
                {isFetchingRatingsReviewerTypes ? (
                  <Spinner centered />
                ) : (
                  <>
                    {!isStandardDeveloper && (
                      <>
                        {getFilteredDetails(
                          activeDevs,
                          datePeriod,
                          reviewerType,
                          true,
                        )}
                        <RatingsByChart
                          data={ratingsReviewersTypeData}
                          gridRef={ratingsReviewerTypeGrid?.current}
                          reviewBenchmarkData={reviewerTypesBenchmarkData}
                          dataMap={REVIEWER_TYPES}
                          labelsMap={RATING_TYPES_DETAILED}
                          hasBenchmark
                          developmentType={globalBenchmarkType}
                        />
                        {chartSource()}
                      </>
                    )}
                    {isStandardDeveloper && (
                      <div className={classes.chartPlaceholder}>
                        <img
                          src="assets/images/charts/chart-ratings-by-type-greyed.png"
                          alt="Chart placeholder"
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} ref={developmentScoresTableGrid}>
              <Card
                className={classes.card}
                eventName="SELECTED_DEVELOPMENT_SCORES_CHART"
              >
                {isStandardDeveloper ? (
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-added-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                ) : (
                  <>
                    <DevelopmentScoresTableContainer
                      gridRef={developmentScoresTableGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      filtersDetails={getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                      appliedFiltersData={getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                        [],
                        true,
                      )}
                      singleDevelopmentScoresChart={
                        singleDevelopmentScoresChart
                      }
                      developmentType={globalBenchmarkType}
                    />
                    {chartSource()}
                  </>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} ref={btrQuestionsGrid}>
              {!isStandardDeveloper && (
                <Card
                  className={classes.card}
                  eventName="BTR_ADDITIONAL_QUESTIONS_CHART"
                >
                  <>
                    <BtrAdditionalQuestionsChart
                      gridRef={btrQuestionsGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      isBtr={true}
                      filtersDetails={getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                    />
                    {chartSource()}
                  </>
                </Card>
              )}
              {isStandardDeveloper && (
                <Card
                  className={classes.card}
                  header={
                    <LockedContentHeader isLocked={isStandardDeveloper}>
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Build to Rent additional questions
                      </Typography>
                    </LockedContentHeader>
                  }
                >
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-BTR-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                </Card>
              )}
            </Grid>
            <Grid item xs={12} sm={6} ref={additionsQuestionsGrid}>
              {!isStandardDeveloper && (
                <Card
                  className={classes.card}
                  eventName="PRIVATE_TENANT_ADDITIONAL_QUESTIONS_CHART"
                  header={
                    <div className="h-exportableDataTitle">
                      <Typography variant="h3">
                        Private tenant additional questions (Build to Rent)
                      </Typography>
                      <ScreenshotButton />
                    </div>
                  }
                >
                  <>
                    {getFilteredDetails(
                      activeDevs,
                      datePeriod,
                      reviewerType,
                      true,
                    )}
                    <AdditionalQuestionsChart
                      gridRef={additionsQuestionsGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      isBtr={true}
                    />
                    {chartSource()}
                  </>
                </Card>
              )}
              {isStandardDeveloper && (
                <Card
                  className={classes.card}
                  header={
                    <LockedContentHeader isLocked={isStandardDeveloper}>
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Private tenant additional questions (Build to Rent)
                      </Typography>
                    </LockedContentHeader>
                  }
                >
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-added-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                </Card>
              )}
            </Grid>
            <Grid item xs={12} sm={6} ref={btsQuestionsGrid}>
              <Card
                className={classes.card}
                eventName="OWNER_ADDITIONAL_QUESTIONS_CHART"
                header={
                  isStandardDeveloper ? (
                    <LockedContentHeader isLocked={isStandardDeveloper}>
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Owner additional questions
                      </Typography>
                    </LockedContentHeader>
                  ) : (
                    ''
                  )
                }
              >
                {isStandardDeveloper ? (
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-BTR-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                ) : (
                  <>
                    <BtrAdditionalQuestionsChart
                      gridRef={btsQuestionsGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      isBtr={false}
                      filtersDetails={getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                    />
                    {chartSource()}
                  </>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} ref={btsAdditionsQuestionsGrid}>
              <Card
                className={classes.card}
                eventName="BTS_ADDITIONAL_QUESTIONS_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Owner additional questions
                      </Typography>
                      <ScreenshotButton />
                    </div>
                  </LockedContentHeader>
                }
              >
                {isStandardDeveloper ? (
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-added-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                ) : (
                  <>
                    {getFilteredDetails(
                      activeDevs,
                      datePeriod,
                      reviewerType,
                      true,
                    )}
                    <AdditionalQuestionsChart
                      gridRef={btsAdditionsQuestionsGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      isBtr={false}
                    />
                    {chartSource()}
                  </>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} ref={ownerReviewsGrid}>
              <Card className={classes.card} eventName="OWNER_REVIEWS_CHART">
                {isStandardDeveloper ? (
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-added-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                ) : (
                  <>
                    <OwnerReviewsChart
                      gridRef={ownerReviewsGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      filtersDetails={getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                      overallRatingHistoryChart={
                        overallRatingHistoryChart?.current
                      }
                    />
                    {chartSource()}
                  </>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} ref={overallRatingHistoryChart}>
              <Card className={classes.card} eventName="RATINGS_HISTORY_CHART">
                <>
                  {!isStandardDeveloper && (
                    <>
                      <OverallRatingHistoryChart
                        gridRef={overallRatingHistoryChart?.current}
                        activeFilters={activeFilters}
                        graphDisabled={graphDisabled || isStandardDeveloper}
                        isStandardDeveloper={isStandardDeveloper}
                        filtersDetails={getFilteredDetails(
                          activeDevs,
                          datePeriod,
                          reviewerType,
                          true,
                        )}
                      />
                      {chartSource()}
                    </>
                  )}
                  {isStandardDeveloper && (
                    <div className={classes.chartPlaceholder}>
                      <img
                        src="assets/images/charts/chart-ratings-history-greyed.png"
                        alt="Chart placeholder"
                      />
                    </div>
                  )}
                </>
              </Card>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              ref={reviewerAgesGrid}
              className={clsx(
                classes.donutChartGrid,
                !hasAgeRangeDonutChart() && classes.gridHidden,
              )}
            >
              <Card
                className={classes.card}
                eventName="SUSTAINABILITY_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Reviewer Ages
                      </Typography>
                      <ScreenshotButton />
                    </div>
                  </LockedContentHeader>
                }
              >
                {isFetchingReviewerAges ? (
                  <Spinner centered />
                ) : (
                  <>
                    {!isStandardDeveloper && (
                      <>
                        {getFilteredDetails(
                          activeDevs,
                          datePeriod,
                          reviewerType,
                          true,
                        )}
                        <DonutChart
                          data={reviewerAgesData}
                          gridRef={reviewerAgesGrid?.current}
                          chartType="reviewerAges"
                        />
                        <Box marginTop={3} marginBottom={-1}>
                          <Typography>
                            Data collected from February 2021
                          </Typography>
                        </Box>
                        {chartSource()}
                      </>
                    )}
                    {isStandardDeveloper && (
                      <div className={classes.chartPlaceholder}>
                        <img
                          src="assets/images/charts/chart-reviewer-types-greyed.png"
                          alt="Chart placeholder"
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Grid>
            <Grid
              item
              xs={12}
              sm={!hasAgeRangeDonutChart() ? 12 : 6}
              ref={ratingsByAgeGrid}
            >
              <Card
                className={classes.card}
                eventName="RATINGS_BY_AGE_RANGE_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Ratings by Age Range
                      </Typography>
                      <ScreenshotButton />
                    </div>
                  </LockedContentHeader>
                }
              >
                {isFetchingRatingsByReviewerAge ? (
                  <Spinner centered />
                ) : (
                  <>
                    {!isStandardDeveloper && (
                      <>
                        {getFilteredDetails(
                          activeDevs,
                          datePeriod,
                          reviewerType,
                          true,
                        )}
                        <RatingsByChart
                          data={ratingsByReviewerAgeData}
                          gridRef={ratingsByAgeGrid?.current}
                          reviewBenchmarkData={null}
                          dataMap={REVIEWER_AGES}
                          labelsMap={RATING_TYPES_DETAILED}
                          chartType="ageRange"
                        />
                        <Box marginTop={3} marginBottom={-1}>
                          <Typography>
                            Data collected from February 2021
                          </Typography>
                        </Box>
                        {chartSource()}
                      </>
                    )}
                    {isStandardDeveloper && (
                      <div className={classes.chartPlaceholder}>
                        <img
                          src="assets/images/charts/chart-ratings-by-type-greyed.png"
                          alt="Chart placeholder"
                        />
                      </div>
                    )}
                  </>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} ref={sustainabilityChartGrid}>
              <Card className={classes.card} eventName="SUSTAINABILITY_CHART">
                {isStandardDeveloper ? (
                  <div className={classes.chartPlaceholder}>
                    <img
                      src="assets/images/charts/chart-added-questions-greyed.png"
                      alt="Chart placeholder"
                    />
                  </div>
                ) : (
                  <>
                    <SustainabilityChart
                      gridRef={sustainabilityChartGrid?.current}
                      activeFilters={activeFilters}
                      graphDisabled={graphDisabled || isStandardDeveloper}
                      filtersDetails={getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                    />
                    {chartSource()}
                  </>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} ref={reviewsHistoryGrid}>
              <Card
                className={classes.card}
                eventName="REVIEWS_HISTORY_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Reviews History
                      </Typography>
                      <ScreenshotButton />
                      <Tooltip
                        title="Reviews History"
                        text="Find out how many reviews your developments have received over time."
                      />
                    </div>
                  </LockedContentHeader>
                }
              >
                <>
                  {!isStandardDeveloper && (
                    <>
                      {getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                      <ReviewsHistoryChart
                        gridRef={reviewsHistoryGrid?.current}
                        activeFilters={activeFilters}
                        graphDisabled={graphDisabled || isStandardDeveloper}
                      />
                      {chartSource()}
                    </>
                  )}
                  {isStandardDeveloper && (
                    <div className={classes.chartPlaceholder}>
                      <img
                        src="assets/images/charts/chart-reviews-history-greyed.png"
                        alt="Chart placeholder"
                      />
                    </div>
                  )}
                </>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} ref={ratingsHistoryGrid}>
              <Card
                className={classes.card}
                eventName="SUBMITTED_REVIEW_SCORES_BY_DATE_CHART"
                header={
                  <LockedContentHeader isLocked={isStandardDeveloper}>
                    <div className="h-exportableDataTitle">
                      <Typography
                        variant="h3"
                        className="l-lockedContent__title"
                      >
                        Submitted Review Scores by Date
                      </Typography>
                      <ScreenshotButton />
                      <Tooltip
                        title="Submitted Review Scores by Date"
                        text="See how your review scores change across the different categories based on your previously submitted reviews."
                      />
                    </div>
                  </LockedContentHeader>
                }
              >
                <>
                  {!isStandardDeveloper && (
                    <>
                      {getFilteredDetails(
                        activeDevs,
                        datePeriod,
                        reviewerType,
                        true,
                      )}
                      <RatingsHistoryChart
                        gridRef={ratingsHistoryGrid?.current}
                        activeFilters={activeFilters}
                        graphDisabled={graphDisabled || isStandardDeveloper}
                      />
                      {chartSource()}
                    </>
                  )}
                  {isStandardDeveloper && (
                    <div className={classes.chartPlaceholder}>
                      <img
                        src="assets/images/charts/chart-ratings-history-greyed.png"
                        alt="Chart placeholder"
                      />
                    </div>
                  )}
                </>
              </Card>
            </Grid>
          </Grid>
        )}

        {mobileFiltersOpened && (
          <Drawer
            open={true}
            anchor="bottom"
            onClose={() => setMobileFiltersOpened(false)}
            className={classes.mobileFiltersDrawer}
          >
            <div className={classes.mobileFiltersHeader}>
              <Typography variant="h3">Filters</Typography>
              <Button
                variant="outlined"
                customColor="dark"
                onClick={handleClearFilters}
              >
                Reset filters
              </Button>
            </div>
            {renderSelectDropdowns()}
          </Drawer>
        )}

        {isMobile && (
          <FloatingButton
            svgIcon="filters"
            variant="extended"
            color="primary"
            onClick={() => setMobileFiltersOpened(true)}
          >
            Filters
          </FloatingButton>
        )}
      </div>
      <Snackbar
        open={snackbarVisible?.open}
        onClose={handleSnackbarClose}
        alertMessage={snackbarVisible?.message}
        status={snackbarVisible?.status}
        content={snackbarVisible?.content}
        closeTime={10000}
      />
    </>
  )
}

export default Dashboard
